import { useState } from 'react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { FeatureFlag } from '@/constants/featureFlags'
import { adminService } from '@/services/adminService'
import type { AdminUserListItem } from '@/types/admin'

interface AdminFeatureFlagsCardProps {
  user: AdminUserListItem
  onSaved: () => void
}

export function AdminFeatureFlagsCard({
  user,
  onSaved,
}: AdminFeatureFlagsCardProps) {
  const [flags, setFlags] = useState<Partial<Record<FeatureFlag, boolean>>>(
    user.featureFlags
  )
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      // Only send enabled flags - unchecked flags are removed entirely
      const enabledFlags = Object.fromEntries(
        Object.entries(flags).filter(([, value]) => value === true)
      )
      await adminService.setFeatureFlags(user.uid, enabledFlags)
      toast.success('Funktionsflaggor sparade')
      onSaved()
    } catch (error) {
      console.error('Failed to save feature flags:', error)
      toast.error('Kunde inte spara funktionsflaggor')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Funktionsflaggor</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!user.hasProfile && (
          <p className="text-sm text-muted-foreground">
            Användaren saknar profildokument — flaggorna skapas när du sparar.
          </p>
        )}
        {Object.values(FeatureFlag).map((flag) => (
          <div key={flag} className="flex items-center justify-between">
            <Label htmlFor={`flag-${flag}`} className="font-mono text-sm">
              {flag}
            </Label>
            <Switch
              id={`flag-${flag}`}
              checked={flags[flag] === true}
              onCheckedChange={(checked) =>
                setFlags((prev) => ({ ...prev, [flag]: checked }))
              }
            />
          </div>
        ))}
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Sparar...' : 'Spara'}
        </Button>
      </CardContent>
    </Card>
  )
}
